import { clamp } from './math';

/**
 * Pure geometry/text helpers behind the motion components. They live apart from the components
 * (react-refresh wants component files to export only components) and are unit-tested directly.
 */

/** Where a magnetic element should sit for a pointer: pulled towards it inside `radius` of its edge, home outside. */
export function magnetOffset(
  rect: { left: number; top: number; width: number; height: number },
  pointer: { x: number; y: number },
  strength: number,
  radius: number,
): { x: number; y: number } {
  const cx = rect.left + rect.width / 2;
  const cy = rect.top + rect.height / 2;
  const dx = pointer.x - cx;
  const dy = pointer.y - cy;
  const reach = Math.max(rect.width, rect.height) / 2 + radius;
  if (Math.hypot(dx, dy) > reach) return { x: 0, y: 0 };
  return { x: dx * strength, y: dy * strength };
}

/** Progress of a pinned track: 0 when its top reaches the viewport top, 1 when its bottom reaches the viewport bottom. */
export function scrubProgress(
  scrollY: number,
  trackTop: number,
  trackHeight: number,
  viewportHeight: number,
): number {
  const distance = trackHeight - viewportHeight;
  if (distance <= 0) return scrollY >= trackTop ? 1 : 0;
  return clamp((scrollY - trackTop) / distance);
}

/** 0 when a surface enters at the bottom of the viewport, 1 when it leaves at the top. */
export function sheenProgress(
  rect: { top: number; height: number },
  viewportHeight: number,
): number {
  const travel = viewportHeight + rect.height;
  return travel <= 0 ? 0 : clamp((viewportHeight - rect.top) / travel);
}

// ── WaveText ────────────────────────────────────────────────────────────────────────────────────────

/** Longest total stagger: long headlines speed up instead of dragging on (DESIGN_SYSTEM MOT-04). */
const MAX_TOTAL_MS = 600;
const CHAR_STEP_MS = 14;

export interface WaveParts {
  /** Words, each a list of animated units (letters in per-letter mode, otherwise the whole word). */
  words: string[][];
  /** Total number of animated units. */
  count: number;
  /** Global index of each word's first unit (the CSS `--i` of its first letter). */
  starts: number[];
}

let segmenter: Intl.Segmenter | null | undefined;
function graphemes(word: string): string[] {
  if (segmenter === undefined) {
    segmenter =
      typeof Intl.Segmenter === 'function'
        ? new Intl.Segmenter(undefined, { granularity: 'grapheme' })
        : null;
  }
  return segmenter ? Array.from(segmenter.segment(word), (part) => part.segment) : Array.from(word);
}

/**
 * Split a headline for the wave. `perChar` animates user-perceived letters (graphemes, so emoji and
 * combining marks stay together); scripts that shape across letters (Devanagari conjuncts) animate
 * per **word** instead, because splitting a conjunct into separate spans would break it.
 */
export function splitForWave(text: string, perChar: boolean): WaveParts {
  const words = text
    .split(/\s+/)
    .filter(Boolean)
    .map((word) => (perChar ? graphemes(word) : [word]));
  const starts: number[] = [];
  let count = 0;
  for (const word of words) {
    starts.push(count);
    count += word.length;
  }
  return { words, count, starts };
}

/** Per-unit delay (ms) that keeps the whole wave within 600 ms. */
export const waveStep = (count: number): number =>
  count <= 1 ? 0 : Math.min(CHAR_STEP_MS, MAX_TOTAL_MS / (count - 1));
